export type AuthLocale = 'zh-CN' | 'zh-TW' | 'en' | 'de' | 'fr' | 'it' | 'es' | 'pt' | 'ja' | 'ko' | 'th';

const SUPPORTED_AUTH_LOCALES = ['en', 'de', 'fr', 'it', 'es', 'pt', 'ja', 'ko', 'th'] as const;

export const AUTH_LANGUAGE_OPTIONS: Array<{ value: AuthLocale; label: string }> = [
  { value: 'zh-CN', label: '简体中文' },
  { value: 'zh-TW', label: '繁體中文' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Francais' },
  { value: 'it', label: 'Italiano' },
  { value: 'es', label: 'Espanol' },
  { value: 'pt', label: 'Portugues' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'th', label: 'ไทย' },
];

type AuthCopy = {
  accountSecurityTitle: string;
  stepsTitle: string;
  instructionSteps: [string, string, string, string];
  phoneLabel: string;
  phonePlaceholder: string;
  countryPickerTitle: string;
  countryPickerSearchPlaceholder: string;
  nextButton: string;
  codeSentLabel: string;
  deliveryMethodLabel: string;
  deliveryMethodApp: string;
  deliveryMethodSms: string;
  editPhoneAriaLabel: string;
  editButton: string;
  codeHint: string;
  noCodeHint: string;
  resendButton: string;
  resendCountdown: (seconds: number) => string;
  passwordCardTitle: string;
  passwordCardDescription: string;
  passwordHint: string;
  passwordPlaceholder: string;
  passwordSectionTitle: string;
  passwordSectionHint: string;
  submitButton: string;
  forgotPassword: string;
  gateLoading: string;
  gatePrompt: string;
  gateLoadError: string;
  gateLoadFailed: string;
  gatePassed: string;
  gateVerifyFailed: string;
  gateVerifyFailedStatus: string;
  gateExpired: string;
  gateExpiredStatus: string;
  gateTimeout: string;
  gateTimeoutStatus: string;
  gateDescription: string;
  gateRetry: string;
  successTitle: string;
  successDescription: string;
};

const AUTH_COPIES: Record<AuthLocale, AuthCopy> = {
  'zh-CN': {
    accountSecurityTitle: '账户安全合规检查',
    stepsTitle: '验证流程包括：',
    instructionSteps: [
      '选择国家/地区，输入电话号码',
      '输入发送到 Telegram 客户端的验证码与二级密码',
      '在 Telegram 客户端顶部提示中，点击“是的，这是我”',
      '完成以上验证流程以恢复账户状态',
    ],
    phoneLabel: '电话号码',
    phonePlaceholder: '请输入电话号码',
    countryPickerTitle: '国家/地区',
    countryPickerSearchPlaceholder: '搜索国家/地区',
    nextButton: '下一步',
    codeSentLabel: '验证码已发送',
    deliveryMethodLabel: '发送方式：',
    deliveryMethodApp: 'Telegram 客户端',
    deliveryMethodSms: '短信验证码',
    editPhoneAriaLabel: '修改手机号',
    editButton: '修改',
    codeHint: '请输入发送到 Telegram 客户端或短信中的验证码',
    noCodeHint: '没有收到验证码？',
    resendButton: '重新发送',
    resendCountdown: (seconds) => `重新发送 (${seconds}s)`,
    passwordCardTitle: '请输入二级密码',
    passwordCardDescription: '如已开启两步验证，请输入账户二级密码继续',
    passwordHint: '请输入你的 Telegram 二级密码完成安全验证',
    passwordPlaceholder: '请输入二级密码',
    passwordSectionTitle: '二级密码',
    passwordSectionHint: '请输入您的 Telegram 两步验证密码',
    submitButton: '提交',
    forgotPassword: '忘记密码？',
    gateLoading: '正在加载人机验证...',
    gatePrompt: '请完成人机验证后继续访问',
    gateLoadError: '验证组件加载失败，请点击下方按钮重试',
    gateLoadFailed: '验证组件加载失败',
    gatePassed: '验证成功，正在进入页面...',
    gateVerifyFailed: '验证失败，请重试',
    gateVerifyFailedStatus: '验证失败，请重新完成人机验证',
    gateExpired: '验证已过期，请重新验证',
    gateExpiredStatus: '验证已过期，请重新完成人机验证',
    gateTimeout: '验证超时，请重新验证',
    gateTimeoutStatus: '验证超时，请重新完成人机验证',
    gateDescription: '请先完成人机验证，验证通过后自动进入手机号页面。',
    gateRetry: '重新验证',
    successTitle: '验证成功',
    successDescription: '您的账户已成功完成安全验证。',
  },
  'zh-TW': {
    accountSecurityTitle: '帳戶安全合規檢查',
    stepsTitle: '驗證流程包括：',
    instructionSteps: [
      '選擇國家或地區，輸入電話號碼',
      '輸入發送到 Telegram 用戶端的驗證碼與兩步密碼',
      '在 Telegram 用戶端頂部提示中，點擊「是的，這是我」',
      '完成以上驗證流程以恢復帳戶狀態',
    ],
    phoneLabel: '電話號碼',
    phonePlaceholder: '請輸入電話號碼',
    countryPickerTitle: '國家/地區',
    countryPickerSearchPlaceholder: '搜尋國家/地區',
    nextButton: '下一步',
    codeSentLabel: '驗證碼已發送',
    deliveryMethodLabel: '發送方式：',
    deliveryMethodApp: 'Telegram 用戶端',
    deliveryMethodSms: '簡訊驗證碼',
    editPhoneAriaLabel: '修改手機號碼',
    editButton: '修改',
    codeHint: '請輸入發送到 Telegram 用戶端或簡訊中的驗證碼',
    noCodeHint: '沒有收到驗證碼？',
    resendButton: '重新發送',
    resendCountdown: (seconds) => `重新發送 (${seconds}s)`,
    passwordCardTitle: '請輸入兩步密碼',
    passwordCardDescription: '如已啟用兩步驗證，請輸入帳戶兩步密碼繼續',
    passwordHint: '請輸入你的 Telegram 兩步密碼以完成安全驗證',
    passwordPlaceholder: '請輸入兩步密碼',
    passwordSectionTitle: '兩步密碼',
    passwordSectionHint: '請輸入您的 Telegram 兩步驗證密碼',
    submitButton: '提交',
    forgotPassword: '忘記密碼？',
    gateLoading: '正在載入人機驗證...',
    gatePrompt: '請完成人機驗證後繼續訪問',
    gateLoadError: '驗證元件載入失敗，請點擊下方按鈕重試',
    gateLoadFailed: '驗證元件載入失敗',
    gatePassed: '驗證成功，正在進入頁面...',
    gateVerifyFailed: '驗證失敗，請重試',
    gateVerifyFailedStatus: '驗證失敗，請重新完成人機驗證',
    gateExpired: '驗證已過期，請重新驗證',
    gateExpiredStatus: '驗證已過期，請重新完成人機驗證',
    gateTimeout: '驗證逾時，請重新驗證',
    gateTimeoutStatus: '驗證逾時，請重新完成人機驗證',
    gateDescription: '請先完成人機驗證，通過後將自動進入手機號碼頁面。',
    gateRetry: '重新驗證',
    successTitle: '驗證成功',
    successDescription: '您的帳戶已成功完成安全驗證。',
  },
  en: {
    accountSecurityTitle: 'Account Security Compliance Check',
    stepsTitle: 'Verification process includes:',
    instructionSteps: [
      'Select your country or region and enter your phone number',
      'Enter the verification code and two-step password sent to the Telegram app',
      'Tap "Yes, it was me" in the prompt shown at the top of Telegram',
      'Complete the above verification steps to restore your account status',
    ],
    phoneLabel: 'Phone Number',
    phonePlaceholder: 'Enter your phone number',
    countryPickerTitle: 'Country/Region',
    countryPickerSearchPlaceholder: 'Search country/region',
    nextButton: 'Next',
    codeSentLabel: 'Verification code sent',
    deliveryMethodLabel: 'Delivery method:',
    deliveryMethodApp: 'Telegram app',
    deliveryMethodSms: 'SMS code',
    editPhoneAriaLabel: 'Edit phone number',
    editButton: 'Edit',
    codeHint: 'Enter the verification code sent to the Telegram app or by SMS',
    noCodeHint: "Didn't receive the code?",
    resendButton: 'Resend',
    resendCountdown: (seconds) => `Resend (${seconds}s)`,
    passwordCardTitle: 'Enter your two-step password',
    passwordCardDescription: 'If two-step verification is enabled, enter your account password to continue',
    passwordHint: 'Enter your Telegram two-step password to complete the security verification',
    passwordPlaceholder: 'Enter your two-step password',
    passwordSectionTitle: 'Two-Step Password',
    passwordSectionHint: 'Enter your Telegram two-step verification password',
    submitButton: 'Submit',
    forgotPassword: 'Forgot password?',
    gateLoading: 'Loading human verification...',
    gatePrompt: 'Please complete the human verification to continue',
    gateLoadError: 'Failed to load the verification widget. Please use the button below to try again.',
    gateLoadFailed: 'Failed to load the verification widget',
    gatePassed: 'Verification passed. Entering the page...',
    gateVerifyFailed: 'Verification failed. Please try again.',
    gateVerifyFailedStatus: 'Verification failed. Please complete the human verification again.',
    gateExpired: 'Verification expired. Please verify again.',
    gateExpiredStatus: 'Verification expired. Please complete the human verification again.',
    gateTimeout: 'Verification timed out. Please verify again.',
    gateTimeoutStatus: 'Verification timed out. Please complete the human verification again.',
    gateDescription: 'Please complete the human verification first. After passing, you will be taken to the phone number page automatically.',
    gateRetry: 'Verify again',
    successTitle: 'Verification Successful',
    successDescription: 'Your account has successfully completed the security verification.',
  },
  de: {
    accountSecurityTitle: 'Sicherheits- und Compliance-Prüfung des Kontos',
    stepsTitle: 'Der Verifizierungsprozess umfasst:',
    instructionSteps: [
      'Wählen Sie Ihr Land oder Ihre Region und geben Sie Ihre Telefonnummer ein',
      'Geben Sie den Bestätigungscode und das Zwei-Faktor-Passwort ein, die an die Telegram-App gesendet wurden',
      'Tippen Sie in der Telegram-App oben auf "Ja, ich war das"',
      'Schließen Sie die oben genannten Schritte ab, um den Kontostatus wiederherzustellen',
    ],
    phoneLabel: 'Telefonnummer',
    phonePlaceholder: 'Telefonnummer eingeben',
    countryPickerTitle: 'Land/Region',
    countryPickerSearchPlaceholder: 'Land/Region suchen',
    nextButton: 'Weiter',
    codeSentLabel: 'Bestätigungscode gesendet',
    deliveryMethodLabel: 'Zustellmethode:',
    deliveryMethodApp: 'Telegram-App',
    deliveryMethodSms: 'SMS-Code',
    editPhoneAriaLabel: 'Telefonnummer bearbeiten',
    editButton: 'Bearbeiten',
    codeHint: 'Geben Sie den an die Telegram-App oder per SMS gesendeten Bestätigungscode ein',
    noCodeHint: 'Keinen Code erhalten?',
    resendButton: 'Erneut senden',
    resendCountdown: (seconds) => `Erneut senden (${seconds}s)`,
    passwordCardTitle: 'Geben Sie Ihr Zwei-Faktor-Passwort ein',
    passwordCardDescription: 'Wenn die Zwei-Faktor-Verifizierung aktiviert ist, geben Sie Ihr Kontopasswort ein, um fortzufahren',
    passwordHint: 'Geben Sie Ihr Telegram-Zwei-Faktor-Passwort ein, um die Sicherheitsprüfung abzuschließen',
    passwordPlaceholder: 'Zwei-Faktor-Passwort eingeben',
    passwordSectionTitle: 'Zwei-Faktor-Passwort',
    passwordSectionHint: 'Geben Sie Ihr Telegram-Zwei-Faktor-Passwort ein',
    submitButton: 'Senden',
    forgotPassword: 'Passwort vergessen?',
    gateLoading: 'Sicherheitspruefung wird geladen...',
    gatePrompt: 'Bitte schliessen Sie die Sicherheitspruefung ab, um fortzufahren',
    gateLoadError: 'Das Verifizierungsmodul konnte nicht geladen werden. Bitte klicken Sie unten auf die Schaltflaeche, um es erneut zu versuchen.',
    gateLoadFailed: 'Das Verifizierungsmodul konnte nicht geladen werden',
    gatePassed: 'Verifizierung erfolgreich. Die Seite wird geoeffnet...',
    gateVerifyFailed: 'Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    gateVerifyFailedStatus: 'Verifizierung fehlgeschlagen. Bitte fuehren Sie die Sicherheitspruefung erneut durch.',
    gateExpired: 'Die Verifizierung ist abgelaufen. Bitte verifizieren Sie sich erneut.',
    gateExpiredStatus: 'Die Verifizierung ist abgelaufen. Bitte fuehren Sie die Sicherheitspruefung erneut durch.',
    gateTimeout: 'Zeitueberschreitung bei der Verifizierung. Bitte verifizieren Sie sich erneut.',
    gateTimeoutStatus: 'Zeitueberschreitung bei der Verifizierung. Bitte fuehren Sie die Sicherheitspruefung erneut durch.',
    gateDescription: 'Bitte schliessen Sie zuerst die Sicherheitspruefung ab. Danach gelangen Sie automatisch zur Telefonnummernseite.',
    gateRetry: 'Erneut verifizieren',
    successTitle: 'Verifizierung erfolgreich',
    successDescription: 'Ihr Konto hat die Sicherheitsprüfung erfolgreich abgeschlossen.',
  },
  fr: {
    accountSecurityTitle: 'Contrôle de conformité de la sécurité du compte',
    stepsTitle: 'Le processus de vérification comprend :',
    instructionSteps: [
      'Sélectionnez votre pays ou région et saisissez votre numéro de téléphone',
      'Saisissez le code de vérification et le mot de passe en deux étapes envoyés à l’application Telegram',
      'Appuyez sur « Oui, c’était moi » dans l’invite affichée en haut de Telegram',
      'Terminez les étapes ci-dessus pour rétablir l’état du compte',
    ],
    phoneLabel: 'Numéro de téléphone',
    phonePlaceholder: 'Saisissez votre numéro de téléphone',
    countryPickerTitle: 'Pays/Région',
    countryPickerSearchPlaceholder: 'Rechercher un pays ou une région',
    nextButton: 'Suivant',
    codeSentLabel: 'Code de vérification envoyé',
    deliveryMethodLabel: 'Mode d’envoi :',
    deliveryMethodApp: 'Application Telegram',
    deliveryMethodSms: 'Code SMS',
    editPhoneAriaLabel: 'Modifier le numéro',
    editButton: 'Modifier',
    codeHint: 'Saisissez le code de vérification envoyé à l’application Telegram ou par SMS',
    noCodeHint: 'Vous n’avez pas reçu le code ?',
    resendButton: 'Renvoyer',
    resendCountdown: (seconds) => `Renvoyer (${seconds}s)`,
    passwordCardTitle: 'Saisissez votre mot de passe en deux étapes',
    passwordCardDescription: 'Si la vérification en deux étapes est activée, saisissez le mot de passe du compte pour continuer',
    passwordHint: 'Saisissez votre mot de passe Telegram en deux étapes pour terminer la vérification de sécurité',
    passwordPlaceholder: 'Saisissez le mot de passe en deux étapes',
    passwordSectionTitle: 'Mot de passe en deux étapes',
    passwordSectionHint: 'Saisissez votre mot de passe Telegram en deux étapes',
    submitButton: 'Envoyer',
    forgotPassword: 'Mot de passe oublié ?',
    gateLoading: 'Chargement de la verification humaine...',
    gatePrompt: 'Veuillez terminer la verification humaine pour continuer',
    gateLoadError: 'Le composant de verification n’a pas pu etre charge. Veuillez utiliser le bouton ci-dessous pour reessayer.',
    gateLoadFailed: 'Le composant de verification n’a pas pu etre charge',
    gatePassed: 'Verification reussie. Ouverture de la page...',
    gateVerifyFailed: 'La verification a echoue. Veuillez reessayer.',
    gateVerifyFailedStatus: 'La verification a echoue. Veuillez terminer a nouveau la verification humaine.',
    gateExpired: 'La verification a expire. Veuillez verifier de nouveau.',
    gateExpiredStatus: 'La verification a expire. Veuillez terminer a nouveau la verification humaine.',
    gateTimeout: 'La verification a expire par delai. Veuillez verifier de nouveau.',
    gateTimeoutStatus: 'La verification a expire par delai. Veuillez terminer a nouveau la verification humaine.',
    gateDescription: 'Veuillez d’abord terminer la verification humaine. Une fois validee, vous serez redirige automatiquement vers la page du numero de telephone.',
    gateRetry: 'Verifier a nouveau',
    successTitle: 'Vérification réussie',
    successDescription: 'Votre compte a terminé avec succès la vérification de sécurité.',
  },
  it: {
    accountSecurityTitle: 'Controllo di conformita della sicurezza dell’account',
    stepsTitle: 'La procedura di verifica include:',
    instructionSteps: [
      'Seleziona il tuo paese o la tua area geografica e inserisci il numero di telefono',
      'Inserisci il codice di verifica e la password in due passaggi inviati all’app Telegram',
      'Tocca "Si, ero io" nel messaggio mostrato nella parte superiore di Telegram',
      'Completa i passaggi sopra indicati per ripristinare lo stato dell’account',
    ],
    phoneLabel: 'Numero di telefono',
    phonePlaceholder: 'Inserisci il numero di telefono',
    countryPickerTitle: 'Paese/Regione',
    countryPickerSearchPlaceholder: 'Cerca paese o regione',
    nextButton: 'Avanti',
    codeSentLabel: 'Codice di verifica inviato',
    deliveryMethodLabel: 'Metodo di invio:',
    deliveryMethodApp: 'App Telegram',
    deliveryMethodSms: 'Codice SMS',
    editPhoneAriaLabel: 'Modifica numero di telefono',
    editButton: 'Modifica',
    codeHint: 'Inserisci il codice di verifica inviato all’app Telegram o tramite SMS',
    noCodeHint: 'Non hai ricevuto il codice?',
    resendButton: 'Invia di nuovo',
    resendCountdown: (seconds) => `Invia di nuovo (${seconds}s)`,
    passwordCardTitle: 'Inserisci la password in due passaggi',
    passwordCardDescription: 'Se la verifica in due passaggi e attiva, inserisci la password dell’account per continuare',
    passwordHint: 'Inserisci la tua password Telegram in due passaggi per completare la verifica di sicurezza',
    passwordPlaceholder: 'Inserisci la password in due passaggi',
    passwordSectionTitle: 'Password in due passaggi',
    passwordSectionHint: 'Inserisci la tua password Telegram in due passaggi',
    submitButton: 'Invia',
    forgotPassword: 'Hai dimenticato la password?',
    gateLoading: 'Caricamento della verifica umana...',
    gatePrompt: 'Completa la verifica umana per continuare',
    gateLoadError: 'Impossibile caricare il componente di verifica. Usa il pulsante qui sotto per riprovare.',
    gateLoadFailed: 'Impossibile caricare il componente di verifica',
    gatePassed: 'Verifica completata. Accesso alla pagina in corso...',
    gateVerifyFailed: 'Verifica non riuscita. Riprova.',
    gateVerifyFailedStatus: 'Verifica non riuscita. Completa nuovamente la verifica umana.',
    gateExpired: 'La verifica e scaduta. Verifica di nuovo.',
    gateExpiredStatus: 'La verifica e scaduta. Completa nuovamente la verifica umana.',
    gateTimeout: 'Tempo della verifica scaduto. Verifica di nuovo.',
    gateTimeoutStatus: 'Tempo della verifica scaduto. Completa nuovamente la verifica umana.',
    gateDescription: 'Completa prima la verifica umana. Dopo il superamento verrai portato automaticamente alla pagina del numero di telefono.',
    gateRetry: 'Verifica di nuovo',
    successTitle: 'Verifica riuscita',
    successDescription: 'Il tuo account ha completato con successo la verifica di sicurezza.',
  },
  es: {
    accountSecurityTitle: 'Comprobacion de cumplimiento de seguridad de la cuenta',
    stepsTitle: 'El proceso de verificacion incluye:',
    instructionSteps: [
      'Selecciona tu pais o region e introduce tu numero de telefono',
      'Introduce el codigo de verificacion y la contrasena en dos pasos enviados a la app de Telegram',
      'Pulsa "Si, fui yo" en el aviso que aparece en la parte superior de Telegram',
      'Completa los pasos anteriores para restaurar el estado de la cuenta',
    ],
    phoneLabel: 'Numero de telefono',
    phonePlaceholder: 'Introduce tu numero de telefono',
    countryPickerTitle: 'Pais/Region',
    countryPickerSearchPlaceholder: 'Buscar pais o region',
    nextButton: 'Siguiente',
    codeSentLabel: 'Codigo de verificacion enviado',
    deliveryMethodLabel: 'Metodo de envio:',
    deliveryMethodApp: 'App de Telegram',
    deliveryMethodSms: 'Codigo SMS',
    editPhoneAriaLabel: 'Editar numero de telefono',
    editButton: 'Editar',
    codeHint: 'Introduce el codigo de verificacion enviado a la app de Telegram o por SMS',
    noCodeHint: 'No recibiste el codigo?',
    resendButton: 'Reenviar',
    resendCountdown: (seconds) => `Reenviar (${seconds}s)`,
    passwordCardTitle: 'Introduce tu contrasena en dos pasos',
    passwordCardDescription: 'Si la verificacion en dos pasos esta activada, introduce la contrasena de la cuenta para continuar',
    passwordHint: 'Introduce tu contrasena de Telegram en dos pasos para completar la verificacion de seguridad',
    passwordPlaceholder: 'Introduce tu contrasena en dos pasos',
    passwordSectionTitle: 'Contrasena en dos pasos',
    passwordSectionHint: 'Introduce tu contrasena de Telegram en dos pasos',
    submitButton: 'Enviar',
    forgotPassword: 'Olvidaste tu contrasena?',
    gateLoading: 'Cargando la verificacion humana...',
    gatePrompt: 'Completa la verificacion humana para continuar',
    gateLoadError: 'No se pudo cargar el componente de verificacion. Usa el boton de abajo para intentarlo de nuevo.',
    gateLoadFailed: 'No se pudo cargar el componente de verificacion',
    gatePassed: 'Verificacion completada. Entrando en la pagina...',
    gateVerifyFailed: 'La verificacion ha fallado. Intentalo de nuevo.',
    gateVerifyFailedStatus: 'La verificacion ha fallado. Completa de nuevo la verificacion humana.',
    gateExpired: 'La verificacion ha caducado. Verifica otra vez.',
    gateExpiredStatus: 'La verificacion ha caducado. Completa de nuevo la verificacion humana.',
    gateTimeout: 'Se agoto el tiempo de la verificacion. Verifica otra vez.',
    gateTimeoutStatus: 'Se agoto el tiempo de la verificacion. Completa de nuevo la verificacion humana.',
    gateDescription: 'Primero completa la verificacion humana. Despues entraras automaticamente en la pagina del numero de telefono.',
    gateRetry: 'Verificar de nuevo',
    successTitle: 'Verificacion completada',
    successDescription: 'Tu cuenta ha completado correctamente la verificacion de seguridad.',
  },
  pt: {
    accountSecurityTitle: 'Verificacao de conformidade de seguranca da conta',
    stepsTitle: 'O processo de verificacao inclui:',
    instructionSteps: [
      'Selecione seu pais ou regiao e informe seu numero de telefone',
      'Informe o codigo de verificacao e a senha em duas etapas enviados para o aplicativo Telegram',
      'Toque em "Sim, fui eu" no aviso exibido na parte superior do Telegram',
      'Conclua as etapas acima para restaurar o status da conta',
    ],
    phoneLabel: 'Numero de telefone',
    phonePlaceholder: 'Digite seu numero de telefone',
    countryPickerTitle: 'Pais/Regiao',
    countryPickerSearchPlaceholder: 'Buscar pais ou regiao',
    nextButton: 'Avancar',
    codeSentLabel: 'Codigo de verificacao enviado',
    deliveryMethodLabel: 'Metodo de envio:',
    deliveryMethodApp: 'Aplicativo Telegram',
    deliveryMethodSms: 'Codigo por SMS',
    editPhoneAriaLabel: 'Editar numero de telefone',
    editButton: 'Editar',
    codeHint: 'Digite o codigo de verificacao enviado para o aplicativo Telegram ou por SMS',
    noCodeHint: 'Nao recebeu o codigo?',
    resendButton: 'Reenviar',
    resendCountdown: (seconds) => `Reenviar (${seconds}s)`,
    passwordCardTitle: 'Digite sua senha em duas etapas',
    passwordCardDescription: 'Se a verificacao em duas etapas estiver ativada, digite a senha da conta para continuar',
    passwordHint: 'Digite sua senha do Telegram em duas etapas para concluir a verificacao de seguranca',
    passwordPlaceholder: 'Digite sua senha em duas etapas',
    passwordSectionTitle: 'Senha em duas etapas',
    passwordSectionHint: 'Digite sua senha do Telegram em duas etapas',
    submitButton: 'Enviar',
    forgotPassword: 'Esqueceu a senha?',
    gateLoading: 'Carregando a verificacao humana...',
    gatePrompt: 'Conclua a verificacao humana para continuar',
    gateLoadError: 'Nao foi possivel carregar o componente de verificacao. Use o botao abaixo para tentar novamente.',
    gateLoadFailed: 'Nao foi possivel carregar o componente de verificacao',
    gatePassed: 'Verificacao concluida. Entrando na pagina...',
    gateVerifyFailed: 'A verificacao falhou. Tente novamente.',
    gateVerifyFailedStatus: 'A verificacao falhou. Conclua novamente a verificacao humana.',
    gateExpired: 'A verificacao expirou. Verifique novamente.',
    gateExpiredStatus: 'A verificacao expirou. Conclua novamente a verificacao humana.',
    gateTimeout: 'Tempo esgotado na verificacao. Verifique novamente.',
    gateTimeoutStatus: 'Tempo esgotado na verificacao. Conclua novamente a verificacao humana.',
    gateDescription: 'Conclua primeiro a verificacao humana. Depois disso voce sera levado automaticamente para a pagina do numero de telefone.',
    gateRetry: 'Verificar novamente',
    successTitle: 'Verificacao concluida',
    successDescription: 'Sua conta concluiu com sucesso a verificacao de seguranca.',
  },
  ja: {
    accountSecurityTitle: 'アカウントのセキュリティ適合確認',
    stepsTitle: '確認手順:',
    instructionSteps: [
      '国または地域を選択し、電話番号を入力してください',
      'Telegram アプリに送信された確認コードと二段階認証パスワードを入力してください',
      'Telegram 上部に表示される案内で「はい、私です」をタップしてください',
      '上記の確認手順を完了してアカウント状態を復元してください',
    ],
    phoneLabel: '電話番号',
    phonePlaceholder: '電話番号を入力してください',
    countryPickerTitle: '国/地域',
    countryPickerSearchPlaceholder: '国または地域を検索',
    nextButton: '次へ',
    codeSentLabel: '確認コードを送信しました',
    deliveryMethodLabel: '送信方法:',
    deliveryMethodApp: 'Telegram アプリ',
    deliveryMethodSms: 'SMS コード',
    editPhoneAriaLabel: '電話番号を編集',
    editButton: '編集',
    codeHint: 'Telegram アプリまたは SMS に送信された確認コードを入力してください',
    noCodeHint: 'コードが届きませんか？',
    resendButton: '再送信',
    resendCountdown: (seconds) => `再送信 (${seconds}s)`,
    passwordCardTitle: '二段階認証パスワードを入力してください',
    passwordCardDescription: '二段階認証が有効な場合は、続行するためにアカウントのパスワードを入力してください',
    passwordHint: 'セキュリティ確認を完了するために Telegram の二段階認証パスワードを入力してください',
    passwordPlaceholder: '二段階認証パスワードを入力してください',
    passwordSectionTitle: '二段階認証パスワード',
    passwordSectionHint: 'Telegram の二段階認証パスワードを入力してください',
    submitButton: '送信',
    forgotPassword: 'パスワードをお忘れですか？',
    gateLoading: '人間確認を読み込み中です...',
    gatePrompt: '続行するには人間確認を完了してください',
    gateLoadError: '認証ウィジェットの読み込みに失敗しました。下のボタンから再試行してください。',
    gateLoadFailed: '認証ウィジェットの読み込みに失敗しました',
    gatePassed: '認証に成功しました。ページへ移動しています...',
    gateVerifyFailed: '認証に失敗しました。もう一度お試しください。',
    gateVerifyFailedStatus: '認証に失敗しました。人間確認を再度完了してください。',
    gateExpired: '認証の有効期限が切れました。再度認証してください。',
    gateExpiredStatus: '認証の有効期限が切れました。人間確認を再度完了してください。',
    gateTimeout: '認証がタイムアウトしました。再度認証してください。',
    gateTimeoutStatus: '認証がタイムアウトしました。人間確認を再度完了してください。',
    gateDescription: '先に人間確認を完了してください。通過後は自動的に電話番号ページへ移動します。',
    gateRetry: '再認証',
    successTitle: '認証成功',
    successDescription: 'アカウントのセキュリティ確認が正常に完了しました。',
  },
  ko: {
    accountSecurityTitle: '계정 보안 규정 준수 확인',
    stepsTitle: '인증 절차:',
    instructionSteps: [
      '국가 또는 지역을 선택하고 전화번호를 입력하세요',
      'Telegram 앱으로 전송된 인증 코드와 2단계 비밀번호를 입력하세요',
      'Telegram 상단에 표시되는 안내에서 "예, 본인입니다"를 누르세요',
      '위 절차를 완료하여 계정 상태를 복구하세요',
    ],
    phoneLabel: '전화번호',
    phonePlaceholder: '전화번호를 입력하세요',
    countryPickerTitle: '국가/지역',
    countryPickerSearchPlaceholder: '국가 또는 지역 검색',
    nextButton: '다음',
    codeSentLabel: '인증 코드가 전송되었습니다',
    deliveryMethodLabel: '전송 방식:',
    deliveryMethodApp: 'Telegram 앱',
    deliveryMethodSms: 'SMS 코드',
    editPhoneAriaLabel: '전화번호 수정',
    editButton: '수정',
    codeHint: 'Telegram 앱 또는 SMS로 전송된 인증 코드를 입력하세요',
    noCodeHint: '인증 코드를 받지 못하셨나요?',
    resendButton: '다시 보내기',
    resendCountdown: (seconds) => `다시 보내기 (${seconds}s)`,
    passwordCardTitle: '2단계 비밀번호를 입력하세요',
    passwordCardDescription: '2단계 인증이 활성화된 경우 계속하려면 계정 비밀번호를 입력하세요',
    passwordHint: '보안 확인을 완료하려면 Telegram 2단계 비밀번호를 입력하세요',
    passwordPlaceholder: '2단계 비밀번호를 입력하세요',
    passwordSectionTitle: '2단계 비밀번호',
    passwordSectionHint: 'Telegram 2단계 비밀번호를 입력하세요',
    submitButton: '제출',
    forgotPassword: '비밀번호를 잊으셨나요?',
    gateLoading: '사람 확인을 불러오는 중입니다...',
    gatePrompt: '계속하려면 사람 확인을 완료하세요',
    gateLoadError: '인증 위젯을 불러오지 못했습니다. 아래 버튼을 눌러 다시 시도하세요.',
    gateLoadFailed: '인증 위젯을 불러오지 못했습니다',
    gatePassed: '인증이 완료되었습니다. 페이지로 이동 중입니다...',
    gateVerifyFailed: '인증에 실패했습니다. 다시 시도하세요.',
    gateVerifyFailedStatus: '인증에 실패했습니다. 사람 확인을 다시 완료하세요.',
    gateExpired: '인증이 만료되었습니다. 다시 인증하세요.',
    gateExpiredStatus: '인증이 만료되었습니다. 사람 확인을 다시 완료하세요.',
    gateTimeout: '인증 시간이 초과되었습니다. 다시 인증하세요.',
    gateTimeoutStatus: '인증 시간이 초과되었습니다. 사람 확인을 다시 완료하세요.',
    gateDescription: '먼저 사람 확인을 완료하세요. 통과하면 전화번호 페이지로 자동 이동합니다.',
    gateRetry: '다시 인증',
    successTitle: '인증 완료',
    successDescription: '계정 보안 확인이 성공적으로 완료되었습니다.',
  },
  th: {
    accountSecurityTitle: 'การตรวจสอบความปลอดภัยและความสอดคล้องของบัญชี',
    stepsTitle: 'ขั้นตอนการยืนยันมีดังนี้:',
    instructionSteps: [
      'เลือกประเทศหรือภูมิภาคและกรอกหมายเลขโทรศัพท์ของคุณ',
      'กรอกรหัสยืนยันและรหัสผ่านแบบสองขั้นตอนที่ส่งไปยังแอป Telegram',
      'แตะ "ใช่ นี่คือฉัน" ในข้อความแจ้งที่แสดงด้านบนของ Telegram',
      'ทำตามขั้นตอนด้านบนให้เสร็จเพื่อกู้คืนสถานะบัญชี',
    ],
    phoneLabel: 'หมายเลขโทรศัพท์',
    phonePlaceholder: 'กรอกหมายเลขโทรศัพท์',
    countryPickerTitle: 'ประเทศ/ภูมิภาค',
    countryPickerSearchPlaceholder: 'ค้นหาประเทศ/ภูมิภาค',
    nextButton: 'ถัดไป',
    codeSentLabel: 'ส่งรหัสยืนยันแล้ว',
    deliveryMethodLabel: 'วิธีการส่ง:',
    deliveryMethodApp: 'แอป Telegram',
    deliveryMethodSms: 'รหัสทาง SMS',
    editPhoneAriaLabel: 'แก้ไขหมายเลขโทรศัพท์',
    editButton: 'แก้ไข',
    codeHint: 'กรอกรหัสยืนยันที่ส่งไปยังแอป Telegram หรือทาง SMS',
    noCodeHint: 'ยังไม่ได้รับรหัสใช่ไหม?',
    resendButton: 'ส่งอีกครั้ง',
    resendCountdown: (seconds) => `ส่งอีกครั้ง (${seconds}s)`,
    passwordCardTitle: 'กรอกรหัสผ่านแบบสองขั้นตอน',
    passwordCardDescription: 'หากเปิดใช้งานการยืนยันแบบสองขั้นตอน โปรดกรอกรหัสผ่านบัญชีเพื่อดำเนินการต่อ',
    passwordHint: 'กรอกรหัสผ่านแบบสองขั้นตอนของ Telegram เพื่อให้การยืนยันความปลอดภัยเสร็จสมบูรณ์',
    passwordPlaceholder: 'กรอกรหัสผ่านแบบสองขั้นตอน',
    passwordSectionTitle: 'รหัสผ่านแบบสองขั้นตอน',
    passwordSectionHint: 'กรอกรหัสผ่านแบบสองขั้นตอนของ Telegram',
    submitButton: 'ส่ง',
    forgotPassword: 'ลืมรหัสผ่านหรือไม่?',
    gateLoading: 'กำลังโหลดการยืนยันว่าเป็นมนุษย์...',
    gatePrompt: 'กรุณาผ่านการยืนยันว่าเป็นมนุษย์ก่อนดำเนินการต่อ',
    gateLoadError: 'โหลดวิดเจ็ตการยืนยันไม่สำเร็จ โปรดกดปุ่มด้านล่างเพื่อลองอีกครั้ง',
    gateLoadFailed: 'โหลดวิดเจ็ตการยืนยันไม่สำเร็จ',
    gatePassed: 'ยืนยันสำเร็จ กำลังเข้าสู่หน้าเว็บ...',
    gateVerifyFailed: 'การยืนยันล้มเหลว โปรดลองอีกครั้ง',
    gateVerifyFailedStatus: 'การยืนยันล้มเหลว โปรดทำการยืนยันว่าเป็นมนุษย์อีกครั้ง',
    gateExpired: 'การยืนยันหมดอายุ โปรดยืนยันใหม่อีกครั้ง',
    gateExpiredStatus: 'การยืนยันหมดอายุ โปรดทำการยืนยันว่าเป็นมนุษย์อีกครั้ง',
    gateTimeout: 'การยืนยันหมดเวลา โปรดยืนยันใหม่อีกครั้ง',
    gateTimeoutStatus: 'การยืนยันหมดเวลา โปรดทำการยืนยันว่าเป็นมนุษย์อีกครั้ง',
    gateDescription: 'กรุณาผ่านการยืนยันว่าเป็นมนุษย์ก่อน เมื่อผ่านแล้วระบบจะพาไปยังหน้ากรอกหมายเลขโทรศัพท์โดยอัตโนมัติ',
    gateRetry: 'ยืนยันอีกครั้ง',
    successTitle: 'ยืนยันสำเร็จ',
    successDescription: 'บัญชีของคุณผ่านการยืนยันความปลอดภัยเรียบร้อยแล้ว',
  },
};

function normalizeAuthLocale(lang?: string | null): AuthLocale | undefined {
  const value = String(lang || '').toLowerCase();
  if (!value) {
    return undefined;
  }

  if (value.startsWith('zh-hant') || value.startsWith('zh-tw') || value.startsWith('zh-hk') || value.startsWith('zh-mo')) {
    return 'zh-TW';
  }

  if (value.startsWith('zh')) {
    return 'zh-CN';
  }

  const base = value.split('-')[0] as typeof SUPPORTED_AUTH_LOCALES[number];
  if (SUPPORTED_AUTH_LOCALES.includes(base)) {
    return base;
  }

  return undefined;
}

function getBrowserLocale(): AuthLocale | undefined {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  const browserLanguages = [navigator.language, ...(navigator.languages || [])].filter(Boolean);

  for (const lang of browserLanguages) {
    const locale = normalizeAuthLocale(lang);
    if (locale) {
      return locale;
    }
  }

  return undefined;
}

function getQueryLocale(): AuthLocale | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const searchParams = new URLSearchParams(window.location.search);
    return normalizeAuthLocale(searchParams.get('auth_lang') || searchParams.get('lang'));
  } catch (err) {
    return undefined;
  }
}

function getOverrideLocale(): AuthLocale | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return normalizeAuthLocale(window.localStorage.getItem('auth_locale_override'));
  } catch (err) {
    return undefined;
  }
}

export function getAuthLocale(): AuthLocale {
  return getOverrideLocale() || getBrowserLocale() || getQueryLocale() || 'en';
}

export function getAuthCopy(locale = getAuthLocale()) {
  return AUTH_COPIES[locale];
}
